import { useCallback, useEffect, useState } from 'react'
import deepMerge from 'deepmerge'
import Cache from '@fs/zion-cache'

import { getPedigree as defaultPedigree } from '../../helpers/entities'
import { getPedigree, getPersonObj } from '../../services'
import { CACHE } from '../../helpers'

const cache = new Cache(CACHE.STORE_VISIBILITY)
const siblingCache = new Cache({ storeName: 'sibling_cards', type: 'session' })

function addGenerationsMetadata(
  { metadata, ...rest },
  numGenerations,
  searchFrom = rest.parents,
  currentGeneration = 2
) {
  // This is destructured in order to preserve any existing metadata.
  let response = { metadata: { generations: 1, ...metadata }, ...rest }

  ;[1, 2].forEach((parent) => {
    const which = searchFrom?.[`parent${parent}Pedigree`]
    if (currentGeneration <= numGenerations && which) {
      if (response.metadata.generations < currentGeneration) {
        response.metadata.generations = currentGeneration
      }
      if (which?.parents) {
        response = addGenerationsMetadata(response, numGenerations, which?.parents, currentGeneration + 1)
      }
    }
  })

  return response
}

const updateCanUserAddMetadata = (person, canUserAdd) => {
  person.metadata = { ...person.metadata, canUserAdd }
}

const processChildrenAndSiblings = (response) => {
  response.pairings.forEach(({ childPedigrees, spousePedigree: { person: spouse } = {} }) => {
    childPedigrees?.forEach(({ person: child }) => {
      updateCanUserAddMetadata(child, !child.id)
    })
    if (spouse) {
      updateCanUserAddMetadata(spouse, !spouse.id)
    }
  })
  response.fullSiblingPedigrees.forEach(({ person: sibling }) => {
    updateCanUserAddMetadata(sibling, !sibling.id)
  })
}

// Main function to add canUserAdd metadata
function addCanUserAddMetadata(
  data,
  isSpousePedigree,
  numGenerations,
  searchFrom = data.parents,
  currentGeneration = 1,
  hasDescendant = false
) {
  // Not strictly necessary but does prevent mutating the object that was passed in.
  let response = { ...data }

  if (currentGeneration === 1) {
    // For consistency in the data. 'canUserAdd' will always be false for yourself.
    updateCanUserAddMetadata(response.person, false)
    if (!isSpousePedigree) {
      processChildrenAndSiblings(response)
    }
    return addCanUserAddMetadata(response, isSpousePedigree, numGenerations, searchFrom, currentGeneration + 1, true)
  }

  ;[1, 2].forEach((parent) => {
    const searchParam = `parent${parent}Pedigree`
    const which = searchFrom?.[searchParam]
    const hasId = !!which.person.id

    updateCanUserAddMetadata(which.person, !hasId && hasDescendant)

    if (which?.parents) {
      response = addCanUserAddMetadata(
        response,
        isSpousePedigree,
        numGenerations,
        which?.parents,
        currentGeneration + 1,
        hasId
      )
    }
  })

  return response
}

export { addCanUserAddMetadata }

let hiddenIds = CACHE.HIDDEN_IDS_INITIAL

async function addVisibilityMetadata(
  data,
  isSpousePedigree,
  numGenerations,
  initialHiddenIds,
  searchFrom = data.parents,
  currentGeneration = 1
) {
  let response = { ...data }
  if (currentGeneration === 1) {
    // Use initialHiddenIds from URL if provided, otherwise read from cache
    hiddenIds =
      initialHiddenIds !== undefined
        ? initialHiddenIds
        : (await cache.getItem(CACHE.HIDDEN_IDS(response.person.id))) || CACHE.HIDDEN_IDS_INITIAL

    // For consistency in the data. 'visible' will always be true for yourself.
    response.person.metadata = {
      ...response.person.metadata,
      visibility: {
        visible: !hiddenIds.includes(response.person.id),
        checked: !hiddenIds.includes(response.person.id),
      },
    }
    if (!isSpousePedigree) {
      response.pairings?.forEach(({ childPedigrees, spousePedigree: { person: spouse } = {} }) => {
        childPedigrees?.forEach(({ person: child }) => {
          child.metadata = {
            ...child.metadata,
            visibility: { visible: !hiddenIds.includes(child.id), checked: !hiddenIds.includes(child.id) },
          }
        })

        if (spouse) {
          spouse.metadata = {
            ...spouse.metadata,
            visibility: { visible: !hiddenIds.includes(spouse.id), checked: !hiddenIds.includes(spouse.id) },
          }
        }
      })
      response.fullSiblingPedigrees?.forEach(({ person: sibling }) => {
        sibling.metadata = {
          ...sibling.metadata,
          visibility: {
            visible: !hiddenIds.includes(sibling.id),
            checked: !hiddenIds.includes(sibling.id),
          },
        }
      })
    }

    response = await addVisibilityMetadata(
      response,
      isSpousePedigree,
      numGenerations,
      initialHiddenIds,
      searchFrom,
      currentGeneration + 1
    )

    return response
  }

  ;[1, 2].forEach(async (parent) => {
    const searchParam = `parent${parent}Pedigree`
    const which = searchFrom?.[searchParam]

    which.person.metadata = {
      ...which.person.metadata,
      visibility: { visible: !hiddenIds.includes(which.person.id), checked: !hiddenIds.includes(which.person.id) },
    }

    if (which?.parents) {
      response = await addVisibilityMetadata(
        response,
        isSpousePedigree,
        numGenerations,
        initialHiddenIds,
        which?.parents,
        currentGeneration + 1
      )
    }
  })

  return data
}

const addPofSpousesAndChildren = (pairings) => {
  pairings.forEach((pairing, pairingIndex) => {
    if (pairing?.spousePedigree?.person) {
      const leafNumber = `1.${pairingIndex}`

      pairing.spousePedigree.person.metadata = {
        ...pairings[pairingIndex].spousePedigree.person.metadata,
        leafNumber,
      }
    }

    if (pairing?.childPedigrees?.length) {
      pairing.childPedigrees.forEach(({ person: child }, childIndex) => {
        const leafNumber = `-1.${pairingIndex}.${childIndex}`
        child.metadata = { ...child.metadata, leafNumber }
      })
    }
  })
}

const addPofSiblings = async (pedigree) => {
  const extractChildrenFromParentPedigrees = (parentKey) => {
    return (
      pedigree?.parents?.[parentKey]?.person?.pairings?.flatMap((pairing) =>
        pairing?.children.map((child) => child.personId)
      ) || []
    )
  }

  const parent1Children = extractChildrenFromParentPedigrees('parent1Pedigree')
  const parent2Children = extractChildrenFromParentPedigrees('parent2Pedigree')

  const fullSiblingPedigrees = Array.from(
    new Set(parent1Children.filter((child) => parent2Children.includes(child) && child !== pedigree.person.id))
  )

  const updateFullSiblingPedigrees = async () => {
    const promises = fullSiblingPedigrees.map(async (siblingId, siblingIndex) => {
      // Check cache first
      const cachedData = await siblingCache.getItem(siblingId)

      let data
      if (cachedData) {
        data = cachedData
      } else {
        // Not in cache, fetch from API
        const response = await getPersonObj(siblingId)
        data = response.data
        // Store in cache for future use
        await siblingCache.setItem(siblingId, data)
      }

      const leafNumber = `0.${siblingIndex}`
      return { person: { ...data, metadata: { ...data.metadata, leafNumber } } }
    })

    pedigree.fullSiblingPedigrees = await Promise.all(promises)
  }

  // Call the function
  await updateFullSiblingPedigrees()
}

async function getAllBranches(pedigree, isSpousePedigree) {
  pedigree.person.metadata = { ...pedigree.person.metadata, leafNumber: isSpousePedigree ? '1.0' : '0' }

  if (!isSpousePedigree) {
    addPofSpousesAndChildren(pedigree?.pairings)
    await addPofSiblings(pedigree)
  }

  let leafNumber = 1
  const maxGenerations = 4

  function setLeafNumber(person) {
    if (person) {
      leafNumber++
      person.metadata = {
        ...person.metadata,
        leafNumber: isSpousePedigree ? `1.0.${leafNumber}` : String(leafNumber),
      }
    }
  }

  function getBranches(currentLimb, generation = 2) {
    // Branches are used by the pedigree view and we only want to show the first 4 generations there
    if (generation > maxGenerations) {
      setLeafNumber(currentLimb.parents?.parent1Pedigree?.person)
      setLeafNumber(currentLimb.parents?.parent2Pedigree?.person)
      return []
    }

    const branches = []
    const parent1Pedigree = currentLimb.parents?.parent1Pedigree
    const parent2Pedigree = currentLimb.parents?.parent2Pedigree
    const parent1 = parent1Pedigree?.person
    const parent2 = parent2Pedigree?.person

    if (parent1 || parent2) {
      setLeafNumber(parent1)
      setLeafNumber(parent2)

      const branch = [
        [parent2, getBranches(parent2Pedigree, generation + 1), currentLimb.person, parent1],
        [parent1, getBranches(parent1Pedigree, generation + 1), currentLimb.person, parent2],
      ]

      branches.push(branch)
    }

    return branches
  }

  return [getBranches(pedigree), pedigree]
}

function cleanPedigreeData(data, config) {
  const { includePreferredSpouse } = config

  if (!includePreferredSpouse && data?.pairings?.[0]?.spousePedigree) {
    delete data.pairings[0].spousePedigree
  }

  return data
}

async function compilePedigreeData(data, fillMissingData, isSpousePedigree, numGenerations, initialHiddenIds) {
  let pedigreeWithData = addGenerationsMetadata(data, numGenerations)

  if (fillMissingData) {
    pedigreeWithData = deepMerge(defaultPedigree, pedigreeWithData, {
      arrayMerge: (_, sourceArray) => sourceArray,
    })
  }

  const [branches, pedigreeToSet] = await getAllBranches(pedigreeWithData, isSpousePedigree)
  pedigreeWithData = addCanUserAddMetadata(pedigreeWithData, isSpousePedigree, numGenerations)
  pedigreeWithData = pedigreeToSet
  pedigreeWithData.metadata = { ...pedigreeWithData.metadata, branches }
  pedigreeWithData = await addVisibilityMetadata(
    pedigreeWithData,
    isSpousePedigree,
    numGenerations,
    initialHiddenIds,
    pedigreeWithData.parents,
    1
  )

  return pedigreeWithData
}

// Returns at least a minimal set of data needed to display a
// 4 generation pedigree.  Any "missing" data is
// filled in with default values.
// If there are any errors they will be set in the lastError state
// and can be handled appropriately.
export function usePedigree({
  fillMissingData = true, // If true, will fill in missing data with default values
  generationsUp = 5, // includes pof as a generation
  generationsDown = 0,
  includePedigreeLooping = false,
  includePreferredSpouse = false,
  includeSpouseAncestry = false,
  includeSuggestions = false,
  initialHiddenIds = undefined, // Hidden IDs from URL query params (undefined = use cache, [] = hide none, ['id'] = hide these)
  isSpousePedigree = false,
  personId, // The person's pedigree to be retrieved
  singleParent = false, // Necessary to retrieve children of a single parent
  spouseId, // Will default the the preferred spouse of the focus person if not provided and includeSpouse it true
} = {}) {
  const [pedigree, setPedigree] = useState()
  const [lastError, setLastError] = useState()

  const getPedigreeData = useCallback(() => {
    return getPedigree({
      id: personId,
      spouseId: singleParent || isSpousePedigree ? 'NONE' : spouseId,
      generationsUp,
      generationsDown,
      includeAllSpousesAndChildren: true,
      includeSpouseAncestry,
      includeSuggestions,
      includePedigreeLooping,
    })
  }, [
    generationsDown,
    generationsUp,
    includePedigreeLooping,
    includeSpouseAncestry,
    includeSuggestions,
    isSpousePedigree,
    personId,
    singleParent,
    spouseId,
  ])

  const fetchPedigreeData = useCallback(async () => {
    try {
      const { data } = await getPedigreeData()
      const cleanedData = cleanPedigreeData(data, {
        includePreferredSpouse,
      })
      const compiledData = await compilePedigreeData(
        cleanedData,
        fillMissingData,
        isSpousePedigree,
        generationsUp,
        initialHiddenIds
      )
      setPedigree(compiledData)
      setLastError(null)
    } catch (error) {
      setLastError(error)
    }
  }, [getPedigreeData, fillMissingData, includePreferredSpouse, isSpousePedigree, generationsUp, initialHiddenIds])

  useEffect(() => {
    let canceled = false

    if (personId) {
      fetchPedigreeData().catch((error) => {
        if (!canceled) {
          setLastError(error)
          if (error.name === 'NoSessionError') {
            throw new Error('usePedigree can only be used in authenticated routes.', {
              cause: error,
            })
          }

          if (error.name === 'AccountNotAuthorizedError') {
            setLastError(error)
          }
        }
      })
    }

    return () => {
      canceled = true
      setLastError(null)
    }
  }, [fetchPedigreeData, personId])

  return { pedigree, refreshPedigreeData: fetchPedigreeData, lastError }
}

export default usePedigree
